import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AdminPanel.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = ({ onClose }) => {
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    category: '',
    lat: '',
    lng: '',
    description_kk: '',
    description_ru: '',
    address: '',
    reporter_full_name: '',
    reporter_phone: '',
    is_active: true
  });
  const [photos, setPhotos] = useState([]);

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [eventsResponse, categoriesResponse] = await Promise.all([
        axios.get(`${API}/events`),
        axios.get(`${API}/categories`)
      ]);
      setEvents(eventsResponse.data);
      setCategories(categoriesResponse.data);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Ошибка при загрузке данных');
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      if (editingEvent) {
        const eventData = {
          category: formData.category,
          location: {
            lat: parseFloat(formData.lat),
            lng: parseFloat(formData.lng)
          },
          description_kk: formData.description_kk,
          description_ru: formData.description_ru,
          address: formData.address || null,
          is_active: formData.is_active
        };
        await axios.put(`${API}/events/${editingEvent.id}`, eventData);
        alert('Событие успешно обновлено');
      } else {
        if (!formData.reporter_full_name.trim() || !formData.reporter_phone.trim()) {
          alert('Заполните ФИО и номер телефона');
          setLoading(false);
          return;
        }
        if (photos.length === 0) {
          alert('Добавьте минимум одно фото события');
          setLoading(false);
          return;
        }
        const submitData = new FormData();
        submitData.append('category', formData.category);
        submitData.append('lat', formData.lat);
        submitData.append('lng', formData.lng);
        submitData.append('description_kk', formData.description_kk);
        submitData.append('description_ru', formData.description_ru);
        if (formData.address.trim()) submitData.append('address', formData.address.trim());
        submitData.append('reporter_full_name', formData.reporter_full_name.trim());
        submitData.append('reporter_phone', formData.reporter_phone.trim());
        submitData.append('priority', '1');
        photos.forEach(photo => submitData.append('photos', photo));
        await axios.post(`${API}/events/with-photos`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        alert('Событие успешно создано');
      }

      resetForm();
      loadData();
    } catch (error) {
      console.error('Error saving event:', error);
      alert('Ошибка при сохранении события');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async (eventId) => {
    if (window.confirm('Удалить это событие?')) {
      try {
        setLoading(true);
        await axios.delete(`${API}/events/${eventId}`);
        alert('Событие удалено');
        loadData();
      } catch (error) {
        console.error('Error deleting event:', error);
        alert('Ошибка при удалении события');
      } finally {
        setLoading(false);
      }
    }
  };

  // Edit event
  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      category: event.category,
      lat: event.location.lat.toString(),
      lng: event.location.lng.toString(),
      description_kk: event.description_kk,
      description_ru: event.description_ru,
      address: event.address || '',
      reporter_full_name: event.reporter_full_name || '',
      reporter_phone: event.reporter_phone || '',
      is_active: event.is_active
    });
    setShowAddForm(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      category: '',
      lat: '',
      lng: '',
      description_kk: '',
      description_ru: '',
      address: '',
      reporter_full_name: '',
      reporter_phone: '',
      is_active: true
    });
    setPhotos([]);
    setEditingEvent(null);
    setShowAddForm(false);
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="admin-panel-overlay">
      <div className="admin-panel">
        <div className="admin-header">
          <h2>Админ-панель управления событиями</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="admin-content">
          {/* Add/Edit Form */}
          <div className="admin-section">
            <div className="section-header">
              <h3>{editingEvent ? 'Редактировать событие' : 'Добавить событие'}</h3>
              <button 
                className="toggle-form-btn"
                onClick={() => setShowAddForm(!showAddForm)}
              >
                {showAddForm ? 'Скрыть форму' : 'Показать форму'}
              </button>
            </div>

            {showAddForm && (
              <form className="event-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Категория:</label>
                    <select 
                      name="category" 
                      value={formData.category}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Выберите категорию</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>
                          {cat.icon} {cat.name_ru}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Статус:</label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                      />
                      Активно
                    </label>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Широта:</label>
                    <input
                      type="number"
                      step="0.000001"
                      name="lat"
                      value={formData.lat}
                      onChange={handleInputChange}
                      placeholder="49.7144"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Долгота:</label>
                    <input
                      type="number"
                      step="0.000001"
                      name="lng"
                      value={formData.lng}
                      onChange={handleInputChange}
                      placeholder="84.2724"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group full-width">
                    <label>Описание (Русский):</label>
                    <textarea
                      name="description_ru"
                      value={formData.description_ru}
                      onChange={handleInputChange}
                      placeholder="Описание на русском языке"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group full-width">
                    <label>Описание (Қазақша):</label>
                    <textarea
                      name="description_kk"
                      value={formData.description_kk}
                      onChange={handleInputChange}
                      placeholder="Қазақ тіліндегі сипаттама"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group full-width">
                    <label>Адрес (необязательно):</label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="ул. Примерная, 123"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group full-width">
                    <label>Фамилия Имя Отчество{!editingEvent && ' *'}:</label>
                    <input
                      type="text"
                      name="reporter_full_name"
                      value={formData.reporter_full_name}
                      onChange={handleInputChange}
                      placeholder="Иванов Иван Иванович"
                      required={!editingEvent}
                      data-testid="admin-reporter-fullname-input"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group full-width">
                    <label>Номер телефона{!editingEvent && ' *'}:</label>
                    <input
                      type="tel"
                      name="reporter_phone"
                      value={formData.reporter_phone}
                      onChange={handleInputChange}
                      placeholder="+7 700 123 45 67"
                      required={!editingEvent}
                      data-testid="admin-reporter-phone-input"
                    />
                  </div>
                </div>

                {!editingEvent && (
                  <div className="form-row">
                    <div className="form-group full-width">
                      <label>Фото события *:</label>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => setPhotos(Array.from(e.target.files))}
                        required
                        data-testid="admin-event-photo-input"
                      />
                    </div>
                  </div>
                )}

                <div className="form-actions">
                  <button type="submit" disabled={loading} className="submit-btn">
                    {loading ? 'Сохранение...' : (editingEvent ? 'Обновить' : 'Создать')}
                  </button>
                  {editingEvent && (
                    <button type="button" onClick={resetForm} className="cancel-btn">
                      Отмена
                    </button>
                  )}
                </div>
              </form>
            )}
          </div>

          {/* Events List */}
          <div className="admin-section">
            <h3>Список событий ({events.length})</h3>
            
            {loading ? (
              <div className="loading">Загрузка...</div>
            ) : (
              <div className="events-list">
                {events.length === 0 ? (
                  <div className="no-events">Нет событий</div>
                ) : (
                  events.map(event => {
                    const category = categories.find(cat => cat.id === event.category);
                    return (
                      <div key={event.id} className="event-item">
                        <div className="event-info">
                          <div className="event-header">
                            <span className="event-category">
                              {category?.icon} {category?.name_ru || event.category}
                            </span>
                            <span className={`event-status ${event.is_active ? 'active' : 'inactive'}`}>
                              {event.is_active ? 'Активно' : 'Неактивно'}
                            </span>
                          </div>
                          
                          <div className="event-description">
                            <strong>RU:</strong> {event.description_ru}
                          </div>
                          <div className="event-description">
                            <strong>KK:</strong> {event.description_kk}
                          </div>
                          
                          {event.address && (
                            <div className="event-address">
                              📍 {event.address}
                            </div>
                          )}
                          
                          <div className="event-coordinates">
                            📌 {event.location.lat.toFixed(6)}, {event.location.lng.toFixed(6)}
                          </div>
                          
                          <div className="event-date">
                            📅 {new Date(event.created_at).toLocaleString('ru-RU')}
                          </div>
                        </div>
                        
                        <div className="event-actions">
                          <button 
                            className="edit-btn"
                            onClick={() => handleEdit(event)}
                          >
                            ✏️ Редактировать
                          </button>
                          <button 
                            className="delete-btn"
                            onClick={() => handleDelete(event.id)}
                          >
                            🗑️ Удалить
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;